import { Button, TextRegular, TextSmallRegular } from '@hm/fundamental'
import { mdiRestore } from '@mdi/js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ColorPickerProps } from '@refra/modules/common/types'

export const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  onReset,
  showResetButton = false,
  label,
  isDisabled = false,
  dataCy = 'color-picker',
  isLocalOverride = false
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const colorInputRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    if (!isDisabled) {
      setIsOpen((prev) => !prev)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleReset = () => {
    onReset?.()
    handleClose()
  }

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, handleClickOutside])

  return (
    <div className="flex items-center gap-2" data-cy={dataCy}>
      {label && <TextRegular>{label}</TextRegular>}
      <div className="relative">
        <div
          ref={triggerRef}
          onClick={handleClick}
          className={`w-8 h-8 border border-border rounded cursor-pointer hover:shadow-md transition-shadow ${isDisabled ? 'opacity-50 cursor-default hover:shadow-none' : ''
            }`}
          style={{ backgroundColor: color }}
          data-cy={`${dataCy}-preview`}
        />
        {isLocalOverride && (
          <div
            className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full border border-background"
            data-cy={`${dataCy}-local-indicator`}
          />
        )}

        {isOpen && (
          <div
            ref={popoverRef}
            className="absolute top-full left-0 mt-1 z-50 p-4 bg-background border border-border rounded-md shadow-lg flex flex-col gap-2"
          >
            <TextRegular className="text-text-secondary">
              {t('localColors.selectColor')}
            </TextRegular>
            <TextSmallRegular className="text-text-secondary">
              {t('localColors.localColorHint')}
            </TextSmallRegular>
            <input
              ref={colorInputRef}
              type="color"
              value={color}
              onChange={(e) => onChange(e.target.value)}
              className="w-30 h-10 cursor-pointer border-none p-0 bg-transparent"
              data-cy={`${dataCy}-input`}
            />
            {showResetButton && onReset && (
              <Button
                variant="tertiary"
                icon={mdiRestore}
                onClick={handleReset}
                data-cy={`${dataCy}-reset-btn`}
              >
                {t('localColors.resetToGlobal')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
